import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useWorkspaces } from '@/features/workspaces/hooks';
import { useCurrentWorkspaceRole } from '@/features/workspaces/hooks/useCurrentWorkspaceRole';
import { useCurrentWorkspaceId } from '@/store/auth.store';

export const ConnectionsRouteGuard: React.FC<React.PropsWithChildren> = ({ children }) => {
  const role = useCurrentWorkspaceRole();
  const { isLoading } = useWorkspaces();
  const workspaceId = useCurrentWorkspaceId() ?? 0;
  const queryClient = useQueryClient();
  const authorized = !isLoading && (role === 'OWNER' || role === 'ADMIN');
  const disposeProtectedQueries = useCallback(() => {
    const protectedWorkspaceQuery = (query: { queryKey: readonly unknown[] }) =>
      (query.queryKey[0] === 'connections' || query.queryKey[0] === 'oauth-client-trust') &&
      query.queryKey[1] === workspaceId;
    void queryClient.cancelQueries({ predicate: protectedWorkspaceQuery });
    queryClient.removeQueries({ predicate: protectedWorkspaceQuery });
  }, [queryClient, workspaceId]);

  useEffect(() => {
    if (!authorized) disposeProtectedQueries();
    return disposeProtectedQueries;
  }, [authorized, disposeProtectedQueries]);

  if (isLoading || role === null) {
    return (
      <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 3 }}>
        <CircularProgress size={20} aria-hidden="true" />
        <Typography>Checking workspace access.</Typography>
      </Box>
    );
  }

  if (role !== 'OWNER' && role !== 'ADMIN') {
    return (
      <Alert severity="info">
        <Typography variant="h6">You cannot manage Connections</Typography>
        <Typography>Ask a workspace owner or admin to review connection access.</Typography>
      </Alert>
    );
  }

  return <>{children}</>;
};
